@import '@fontsource/jetbrains-mono/latin-400.css';
@import '@fontsource/jetbrains-mono/latin-500.css';
@import '@fontsource/inter/latin-400.css';
@import '@fontsource/inter/latin-600.css';

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  /*
   * Theme tokens
   * ------------
   * `:root` carries the LIGHT values (the default, since `:root` matches the
   * document root with or without a class). `.dark` carries the DARK values
   * and is applied to <html> by the inline theme script in app/root.tsx.
   *
   * Two parallel token systems coexist on purpose:
   *   1. Design tokens (raw hex / rgba) — referenced from tailwind.config.js
   *      via var(), and from inline styles like text-section-label / halo
   *      gradients. Names start with no prefix.
   *   2. shadcn tokens (HSL triplets) — consumed by Tailwind via
   *      hsl(var(--token)) inside the shadcn-aligned classes
   *      (bg-background, text-foreground, etc.).
   *
   * `video-bg` is intentionally the same in both themes — the video player
   * frame is always black.
   */

  :root {
    /* Design tokens — light */
    --bg-primary: #ffffff;
    --bg-elevated: #f8fafc;
    --bg-surface: #f1f5f9;
    --border-subtle: #e2e8f0;
    --border-active: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    --accent-cyan: #0891b2;
    --accent-hover: #0e7490;
    --accent-dim: rgba(8, 145, 178, 0.08);
    --accent-glow: rgba(8, 145, 178, 0.25);
    --success: #059669;
    --success-dim: rgba(5, 150, 105, 0.08);
    --error: #dc2626;
    --error-dim: rgba(220, 38, 38, 0.08);
    --warning: #d97706;
    --warning-dim: rgba(217, 119, 6, 0.08);
    --video-bg: #000000;

    /* shadcn tokens — light */
    --background: 0 0% 100%;
    --foreground: 222 47% 11%;
    --card: 0 0% 100%;
    --card-foreground: 222 47% 11%;
    --popover: 0 0% 100%;
    --popover-foreground: 222 47% 11%;
    --primary: 189 94% 33%;
    --primary-foreground: 0 0% 100%;
    --secondary: 210 40% 96%;
    --secondary-foreground: 222 47% 11%;
    --muted: 210 40% 96%;
    --muted-foreground: 215 16% 47%;
    --accent: 189 94% 33%;
    --accent-foreground: 0 0% 100%;
    --destructive: 0 72% 51%;
    --destructive-foreground: 0 0% 100%;
    --border: 214 32% 91%;
    --input: 214 32% 91%;
    --ring: 189 94% 33%;
    --radius: 0.625rem;
    --sidebar-background: 0 0% 98%;
    --sidebar-foreground: 222 47% 11%;
    --sidebar-primary: 189 94% 33%;
    --sidebar-primary-foreground: 0 0% 100%;
    --sidebar-accent: 210 40% 96%;
    --sidebar-accent-foreground: 222 47% 11%;
    --sidebar-border: 214 32% 91%;
    --sidebar-ring: 189 94% 33%;

    color-scheme: light;
  }

  .dark {
    /* Design tokens — dark (preserved from the original single-theme build) */
    --bg-primary: #030712;
    --bg-elevated: #0a0f1a;
    --bg-surface: #0f1525;
    --border-subtle: #1a2236;
    --border-active: #2a3650;
    --text-primary: #f0f4f8;
    --text-secondary: #7a8aa0;
    --text-muted: #4a5568;
    --accent-cyan: #22d3ee;
    --accent-hover: #67e8f9;
    --accent-dim: rgba(34, 211, 238, 0.08);
    --accent-glow: rgba(34, 211, 238, 0.25);
    --success: #34d399;
    --success-dim: rgba(52, 211, 153, 0.08);
    --error: #f87171;
    --error-dim: rgba(248, 113, 113, 0.08);
    --warning: #fbbf24;
    --warning-dim: rgba(251, 191, 36, 0.08);
    --video-bg: #000000;

    /* shadcn tokens — dark */
    --background: 222 47% 3%;
    --foreground: 210 40% 96%;
    --card: 222 35% 7%;
    --card-foreground: 210 40% 96%;
    --popover: 222 35% 7%;
    --popover-foreground: 210 40% 96%;
    --primary: 186 93% 53%;
    --primary-foreground: 222 47% 3%;
    --secondary: 220 30% 10%;
    --secondary-foreground: 210 40% 96%;
    --muted: 220 30% 10%;
    --muted-foreground: 215 20% 45%;
    --accent: 186 93% 53%;
    --accent-foreground: 222 47% 3%;
    --destructive: 0 84% 60%;
    --destructive-foreground: 210 40% 96%;
    --border: 220 25% 16%;
    --input: 220 25% 16%;
    --ring: 186 93% 53%;
    --sidebar-background: 222 35% 7%;
    --sidebar-foreground: 210 40% 96%;
    --sidebar-primary: 186 93% 53%;
    --sidebar-primary-foreground: 222 47% 3%;
    --sidebar-accent: 220 30% 10%;
    --sidebar-accent-foreground: 210 40% 96%;
    --sidebar-border: 220 25% 16%;
    --sidebar-ring: 186 93% 53%;

    color-scheme: dark;
  }

  * {
    @apply border-border-subtle;
  }

  html, body {
    background-color: var(--bg-primary);
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--text-primary);
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
}

@layer utilities {
  .text-section-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.6875rem;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--accent-cyan);
  }

  .font-heading {
    font-family: 'JetBrains Mono', monospace;
  }

  .font-body {
    font-family: 'Inter', sans-serif;
  }

  .bg-gradient-halo {
    background: radial-gradient(circle at center, var(--accent-dim) 0%, transparent 70%);
  }
}
